/**
 * HostTrackerAlertsPanel Component
 * 
 * Shows GPS tracker alerts for the host dashboard.
 * Displays: offline trackers on their vehicles, overdue bookings with trackers.
 * Soft alerts only - no enforcement.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  Satellite, 
  AlertTriangle, 
  Clock, 
  RefreshCw,
  ChevronRight,
  Radio,
  Car,
  CheckCircle
} from 'lucide-react';

const HostTrackerAlertsPanel = ({ compact = false }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/host/tracker/alerts');
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch tracker alerts:', err);
      // Don't show error for hosts without tracked vehicles
      if (err.response?.status === 404) {
        setData({ alerts: [], total_alerts: 0, has_tracked_vehicles: false });
      } else {
        setError('Failed to load tracker alerts');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAlerts();
    // Refresh every 5 minutes
    const interval = setInterval(fetchAlerts, 300000);
    return () => clearInterval(interval);
  }, []);

  const getAlertIcon = (type) => {
    switch (type) {
      case 'tracker_offline': return <Radio className="w-4 h-4 text-amber-600" />;
      case 'booking_overdue_tracked': return <Car className="w-4 h-4 text-orange-600" />;
      default: return <AlertTriangle className="w-4 h-4 text-slate-600" />;
    }
  };

  const getAlertBg = (severity, type) => {
    if (severity === 'critical') return 'bg-orange-50 border-orange-300 hover:bg-orange-100';
    return 'bg-amber-50 border-amber-200 hover:bg-amber-100';
  };

  const getAlertTitle = (type) => {
    switch (type) {
      case 'tracker_offline': return 'Tracker Offline';
      case 'booking_overdue_tracked': return 'Overdue Booking';
      default: return 'Alert';
    }
  };

  // Don't render if host has no tracked vehicles
  if (!loading && data && !data.has_tracked_vehicles) {
    return null;
  }

  if (loading && !data) {
    return (
      <Card className="border-slate-200" data-testid="host-tracker-alerts-panel">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Satellite className="w-5 h-5 text-blue-600" />
            Tracker Alerts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <RefreshCw className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50" data-testid="host-tracker-alerts-panel">
        <CardContent className="py-4">
          <p className="text-red-600 text-sm">{error}</p>
          <Button variant="outline" size="sm" onClick={fetchAlerts} className="mt-2">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const { alerts, total_alerts, critical_count, warning_count, tracked_vehicle_count } = data || {};
  const hasCritical = critical_count > 0;
  const hasAlerts = total_alerts > 0;

  // Compact view for sidebar or summary
  if (compact) {
    if (!hasAlerts) return null; // Don't show compact if no alerts
    
    return (
      <div 
        className={`p-3 rounded-lg border ${hasCritical ? 'border-orange-300 bg-orange-50' : 'border-amber-200 bg-amber-50'}`}
        data-testid="host-tracker-alerts-compact"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Satellite className={`w-4 h-4 ${hasCritical ? 'text-orange-600' : 'text-amber-600'}`} />
            <span className="text-sm font-medium text-slate-700">
              {total_alerts} Tracker Alert{total_alerts !== 1 ? 's' : ''}
            </span>
          </div>
          <Badge className={hasCritical ? 'bg-orange-600' : 'bg-amber-500'}>
            {critical_count > 0 ? `${critical_count} critical` : `${warning_count} warning`}
          </Badge>
        </div>
      </div>
    );
  }

  // Full view
  return (
    <Card className="border-slate-200" data-testid="host-tracker-alerts-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Satellite className={`w-5 h-5 ${hasCritical ? 'text-orange-600' : 'text-blue-600'}`} />
            GPS Tracker Alerts
            {hasCritical && (
              <Badge className="bg-orange-600 text-white text-xs">
                {critical_count} Critical
              </Badge>
            )}
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchAlerts}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-1">
          Alerts for your {tracked_vehicle_count} tracked vehicle{tracked_vehicle_count !== 1 ? 's' : ''}
        </p>
      </CardHeader>

      <CardContent className="space-y-3">
        {!hasAlerts ? (
          <div className="flex items-center justify-center gap-2 text-green-600 py-4 bg-green-50 rounded-lg">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium text-sm">All trackers operational</span>
          </div>
        ) : (
          <div className="space-y-2">
            {alerts?.map((alert, idx) => (
              <div
                key={`alert-${idx}`}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${getAlertBg(alert.severity, alert.type)}`}
                onClick={() => alert.action_url && navigate(alert.action_url)}
                data-testid={`host-tracker-alert-${idx}`}
              >
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    {getAlertIcon(alert.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-slate-800">
                        {alert.vehicle_name}
                      </span>
                      <Badge className={
                        alert.severity === 'critical'
                          ? 'bg-orange-100 text-orange-800 text-xs'
                          : 'bg-amber-100 text-amber-800 text-xs'
                      }>
                        {getAlertTitle(alert.type)}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 mt-1">{alert.message}</p>
                    
                    {/* Show additional context for overdue bookings */}
                    {alert.type === 'booking_overdue_tracked' && (
                      <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        <span>End date: {alert.end_date}</span>
                      </div>
                    )}
                  </div>
                  
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="border-amber-300 text-amber-700 hover:bg-amber-100 shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (alert.action_url) navigate(alert.action_url);
                    }}
                  >
                    {alert.action_label || 'View'}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HostTrackerAlertsPanel;
